import OpenAI from 'openai'
import {Button} from 'primereact/button'
import {Dialog} from 'primereact/dialog'
import React, {forwardRef, useImperativeHandle, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {CHAT_KEY} from '../../Constants'
import {getLanguageCode, languageCodeToLanguage} from '../../i18n/LanguageUtils'
import TranslateUtils from '../../i18n/TranslateUtils'
import {
    blockOverflownScroll,
    unblockOverflownScroll,
} from '../../utils/BlockScrollsUtils'
import {observationKeys} from './Constants'
import {convertSkinTempToMBT} from './Utils'

export const ObservationSummary = forwardRef(
    ({currentObsDataList = []}, ref) => {
        useImperativeHandle(ref, () => ({
            showForm: (currentObsDataList, parameterData) =>
                handleShowForm(currentObsDataList, parameterData),
        }))
        const {translateCommons} = TranslateUtils()
        const openai = new OpenAI({
            apiKey: CHAT_KEY,
            dangerouslyAllowBrowser: true,
        })
        const {i18n} = useTranslation()
        const [visible, setVisible] = useState(false)
        const [summary, setSummary] = useState('')
        const [isComplete, setIsComplete] = useState(true)
        const parseKeysData = (keyList, dataList) => {
            let result = {}

            keyList.forEach(key => {
                result[key] = []
            })

            dataList.forEach(data => {
                keyList.forEach(key => {
                    if (
                        (key !== observationKeys.bloodPressure &&
                            data.hasOwnProperty(key) &&
                            data[key]) ||
                        (key === observationKeys.bloodPressure &&
                            data.hasOwnProperty(observationKeys.systolic) &&
                            data.hasOwnProperty(observationKeys.diastolic) &&
                            data[observationKeys.systolic] &&
                            data[observationKeys.diastolic])
                    ) {
                        if (key === observationKeys.temperature) {
                            if (data['device'] === 'W') {
                                result[key].push(
                                    convertSkinTempToMBT(data[key]),
                                )
                            } else {
                                result[key].push(data[key])
                            }
                        } else if (key === observationKeys.bloodPressure) {
                            if (
                                data[observationKeys.diastolic] &&
                                data[observationKeys.systolic]
                            ) {
                                result[key].push(
                                    `diastolic: ${
                                        data[observationKeys.diastolic]
                                    } + '/' + systolic: ${
                                        data[observationKeys.systolic]
                                    }`,
                                )
                            }
                        } else {
                            result[key].push(data[key])
                        }
                    }
                })
            })

            for (let key in result) {
                result[key] = result[key].join(', ')
            }

            return result
        }

        const handleShowForm = async (
            currentObsDataList = [],
            parameterData = {},
        ) => {
            blockOverflownScroll()
            setSummary('')
            setVisible(true)
            setIsComplete(false)

            const dataFromKeys = parseKeysData(
                [
                    observationKeys.heartRate,
                    observationKeys.oxygen,
                    observationKeys.temperature,
                    observationKeys.bloodPressure,
                    observationKeys.respiration,
                    observationKeys.levelOfConsciousness,
                    observationKeys.bloodGlucoseLevel,
                    observationKeys.litres,
                    // observationKeys.painScore,
                ],
                currentObsDataList,
            )
            const observationPromptString = `
                Respiration: ${dataFromKeys[observationKeys.respiration]},
                Heart Rate: ${dataFromKeys[observationKeys.heartRate]}, 
                Sp02: ${dataFromKeys[observationKeys.oxygen]}, 
                Temperature: ${dataFromKeys[observationKeys.temperature]},
                Blood Pressure: ${dataFromKeys[observationKeys.bloodPressure]},
                Level of Consciousness: ${
                    dataFromKeys[observationKeys.levelOfConsciousness]
                },
                Blood Glucose Level: ${
                    dataFromKeys[observationKeys.bloodGlucoseLevel]
                },
                Litres: ${dataFromKeys[observationKeys.litres]},
            `
            // Pain Score: ${dataFromKeys[observationKeys.painScore]}

            let parameterPromptString = ''
            Object.keys(parameterData).forEach(key => {
                parameterPromptString += ` ${key}: ${parameterData[key]}`
            })
            let prompt = `Can you provide a summary of health observations based on the following parameters: ${parameterPromptString} and the following observations: ${observationPromptString}. Seperate each health criteria (a conclusion included) into different paragraphs with bullet points and key header has to be in bold using <h5 class="m-0 p-0 font-bold text-darkcyan"> tag the a conclusion at the end. dont format using <ul> or <li> tags. Translate the response into ${languageCodeToLanguage(
                getLanguageCode(),
            )}`
            const responseChunks = await openai.chat.completions.create({
                messages: [{role: 'user', content: prompt}],
                model: 'gpt-3.5-turbo',
                stream: true,
            })

            for await (const chunk of responseChunks) {
                if (
                    chunk.choices[0].delta.content !== null &&
                    chunk.choices[0].delta.content !== undefined
                ) {
                    const formattedLineBreakContent =
                        chunk.choices[0].delta.content.replace(/\n/g, '<br />')

                    setSummary(
                        prevAiResponse =>
                            prevAiResponse + formattedLineBreakContent,
                    )
                }
            }

            setIsComplete(true)
        }

        const handleHideForm = () => {
            unblockOverflownScroll()
            setSummary('')
            setVisible(false)
        }

        const dateFromTo = useMemo(() => {
            if (!currentObsDataList || currentObsDataList.length === 0) {
                return ''
            }

            const startDate =
                currentObsDataList[0].year +
                '-' +
                currentObsDataList[0].date +
                ' ' +
                currentObsDataList[0].time

            if (currentObsDataList.length === 1) {
                return ` (since ${startDate})`
            }

            const endDate =
                currentObsDataList[currentObsDataList.length - 1].year +
                '-' +
                currentObsDataList[currentObsDataList.length - 1].date +
                ' ' +
                currentObsDataList[currentObsDataList.length - 1].time

            return ` (${startDate} - ${endDate})`
        }, [currentObsDataList])

        const footerContent = (
            <Button
                className="col-12"
                style={{height: '40px'}}
                label="OK, I got it!"
                icon="pi pi-check-circle"
                disabled={!isComplete}
                onClick={() => handleHideForm()}
                autoFocus
            />
        )

        return (
            <React.Fragment>
                <Dialog
                    header={translateCommons('Observation Summary')}
                    visible={visible}
                    closable={isComplete}
                    position={'center'}
                    onHide={() => {
                        if (!visible || !isComplete) return
                        handleHideForm()
                    }}
                    draggable={false}
                    resizable={false}>
                    <div
                        className="mb-3 flex align-items-center px-3 font-bold"
                        style={{
                            background: '#f9f9f9',
                            borderRadius: '8px',
                            minHeight: '3rem',
                        }}>
                        <div className="flex align-items-center text-lg">
                            <div className="mr-3 pt-1">
                                <i className="pi pi-info-circle"></i>
                            </div>
                            <div>
                                {translateCommons(
                                    'Based on the parameters and observations provided',
                                )}
                                {dateFromTo} {' ,'}
                                {translateCommons(
                                    "here is a summary of the patient's health condition:",
                                )}
                            </div>
                        </div>
                    </div>
                    <div
                        className="m-0 mb-5 font-bold"
                        dangerouslySetInnerHTML={{__html: summary}}></div>
                    <div>{footerContent}</div>
                </Dialog>
            </React.Fragment>
        )
    },
)
